define("shared/utils/canvas-video-player", ["exports"], function (_exports) {
  "use strict";

  Object.defineProperty(_exports, "__esModule", {
    value: true
  });
  _exports.default = void 0;
  0; //eaimeta@70e063a35619d71feaimeta@70e063a35619d71f

  /**
   * @class canvasVideoPlayer
   * @extends Utils
   */
  const cvpHandlers = {
    canvasClickHandler: null,
    videoTimeUpdateHandler: null,
    videoCanPlayHandler: null,
    windowResizeHandler: null
  }; // Note: the default options to use for the video player

  const defaultOptions = {
    audio: true,
    // enable audio
    coverTime: 0,
    // time of initial frame to display when video loads
    cropArray: [],
    // crop coordinates of viewable video area
    framesPerSecond: 25,
    // the rate at which the player animates frames
    hideVideo: true,
    // hide underlying video element
    makeLoop: false,
    // loop video when it ends
    pauseOnClick: false,
    // pause video when element is clicked
    resetOnLastFrame: true,
    // reset player to beginning after last frame
    timelineSelector: false,
    // enable timeline represented as a progress bar
    startTime: 0,
    // start time of viewable video
    endTime: null // end time of viewable video

  };
  /**
   * Create a canvas video player which mirrors and replaces an HTML5 video player
   * in cases where on-the-fly video manipulation is required
   *
   * @class CanvasVideoPlayer
   * @param {Object} options initial player configuration values
   *
   * @returns {Object} canvasVideoPlayer
   */

  function CanvasVideoPlayer(options) {
    this.options = Object.assign({}, defaultOptions);
    this.setup(options);

    if (!this.options.videoSelector || !this.video) {
      console.error('No "videoSelector" property, or the element is not found');
      return;
    }

    if (!this.options.canvasSelector || !this.canvas) {
      console.error('No "canvasSelector" property, or the element is not found');
      return;
    }

    if (this.options.timelineSelector && !this.timeline) {
      console.error('Element for the "timelineSelector" selector not found', this.options.timelineSelector);
      return;
    }

    if (this.options.timelineSelector && !this.timelinePassed) {
      console.error('Element for the "timelinePassed" not found');
      return;
    }

    if (this.options.audio) {
      if (typeof this.options.audio === 'string') {
        // Note: Use audio selector from options if specified
        this.audio = document.querySelectorAll(this.options.audio)[0];

        if (!this.audio) {
          console.error('Element for the "audio" not found');
          return;
        }
      } else {
        // Note: Creates audio element which uses same video sources
        this.audio = document.createElement('audio');
        this.audio.innerHTML = this.video.innerHTML;
        this.video.parentNode.insertBefore(this.audio, this.video);
      }

      const iOS = /iPad|iPhone|iPod/.test(navigator.platform);

      if (iOS) {
        // Note: Autoplay doesn't work with audio on iOS
        // User have to manually start the audio
        this.options.autoplay = false;
      }
    }

    this.ctx = this.canvas.getContext('2d');
    this.playing = false;
    this.resizeTimeoutReference = false;
    this.RESIZE_TIMEOUT = 1000;
    this.init();
    this.bind();
  }
  /**
   * initialize canvas video player
   *
   * @method init
   *
   */


  CanvasVideoPlayer.prototype.init = function () {
    this.video.currentTime = this.coverTime;
    this.setCanvasSize();

    if (this.options.hideVideo) {
      this.video.style.display = 'none';
    }
  };
  /**
   * setup player with new options
   *
   * @method setup
   * @param {Object} options configuration values
   *
   */


  CanvasVideoPlayer.prototype.setup = function (options) {
    for (const i in options) {
      this.options[i] = options[i];
    }

    const {
      videoSelector,
      canvasSelector,
      timelineSelector,
      playButtonSelector,
      startTime,
      endTime,
      coverTime,
      hideVideo
    } = this.options;
    this.video = document.querySelector(videoSelector);
    this.canvas = document.querySelector(canvasSelector);
    this.timeline = timelineSelector ? document.querySelector(timelineSelector) : null;
    this.timelinePassed = document.querySelector(timelineSelector + '> div');
    this.playButton = playButtonSelector ? document.querySelector(playButtonSelector) : null;

    if (hideVideo) {
      this.video.style.display = 'none';
    }

    this.hasPlayed = false;
    this.startTime = startTime;
    this.endTime = endTime;
    this.coverTime = coverTime;
  };
  /**
   * seek to a specific time in video
   *
   * @method jumpTo
   * @param {Number} percentage percentage of video duration
   *
   */


  CanvasVideoPlayer.prototype.jumpTo = function (percentage) {
    this.video.currentTime = this.video.duration * percentage;

    if (this.options.audio) {
      this.audio.currentTime = this.audio.duration * percentage;
    }
  };
  /**
   * bind event handlers and set on cvpHandlers object
   *
   * @method bind
   * @private
   *
   */


  CanvasVideoPlayer.prototype.bind = function () {
    const self = this; // Note: Plays or pauses video on canvas click

    if (this.options.pauseOnClick === true) {
      this.canvas.addEventListener('click', cvpHandlers.canvasClickHandler = function () {
        self.playPause();
      });
    } // Note: On every time update draws frame


    this.video.addEventListener('timeupdate', cvpHandlers.videoTimeUpdateHandler = function () {
      self.drawFrame();

      if (self.options.timelineSelector) {
        self.updateTimeline();
      }
    }); // Note: Draws first frame

    this.video.addEventListener('canplay', cvpHandlers.videoCanPlayHandler = function () {
      self.drawFrame();
    }); // Note: To be sure 'canplay' event that isn't already fired

    if (this.video.readyState >= 2) {
      self.drawFrame();
    }

    if (self.options.autoplay) {
      self.play();
    } // Note: Click on the video seek video


    if (self.options.timelineSelector) {
      this.timeline.addEventListener('click', e => {
        const playbackRect = self.timeline.getBoundingClientRect();
        const offset = e.clientX - playbackRect.left;
        const percentage = offset / self.timeline.offsetWidth;
        self.jumpTo(percentage);
      });
    } // Note: Cache canvas size on resize (doing it only once in a second)


    window.addEventListener('resize', cvpHandlers.windowResizeHandler = function () {
      clearTimeout(self.resizeTimeoutReference);
      self.resizeTimeoutReference = setTimeout(() => {
        self.setCanvasSize();
        self.drawFrame();
      }, self.RESIZE_TIMEOUT);
    });

    this.unbind = function () {
      this.pause();
      this.canvas.removeEventListener('click', cvpHandlers.canvasClickHandler);
      this.video.removeEventListener('timeupdate', cvpHandlers.videoTimeUpdateHandler);
      this.video.removeEventListener('canplay', cvpHandlers.videoCanPlayHandler);
      window.removeEventListener('resize', cvpHandlers.windowResizeHandler);

      if (this.options.audio) {
        this.audio.parentNode.removeChild(this.audio);
      }
    };
  };

  CanvasVideoPlayer.prototype.updateTimeline = function () {
    const percentage = (this.video.currentTime * 100 / this.video.duration).toFixed(2);
    this.timelinePassed.style.width = percentage + '%';
  };

  CanvasVideoPlayer.prototype.setCanvasSize = function (width, height) {
    this.width = width || this.canvas.clientWidth;
    this.height = height || this.canvas.clientHeight;
    this.canvas.setAttribute('width', this.width);
    this.canvas.setAttribute('height', this.height);
  };
  /**
   * begin playing video
   *
   * @method play
   *
   */


  CanvasVideoPlayer.prototype.play = function () {
    this.lastTime = window.performance.now() ? window.performance.now() : Date.now();
    this.playing = true;

    if (!this.hasPlayed) {
      this.video.currentTime = this.startTime;
      this.hasPlayed = true;
    }

    this.loop();

    if (this.options.audio) {
      // Resync audio and video
      this.audio.currentTime = this.video.currentTime;
      this.audio.play();
    }

    if (this.playButton) {
      this.playButton.classList.remove('is--play');
      this.playButton.classList.add('is--pause');
    }
  };
  /**
   * pause video at current time
   *
   * @method pause
   *
   */


  CanvasVideoPlayer.prototype.pause = function () {
    this.playing = false;

    if (this.options.audio) {
      this.audio.pause();
    }

    if (this.playButton) {
      this.playButton.classList.remove('is--pause');
      this.playButton.classList.add('is--play');
    }
  };
  /**
   * play or pause video depending on if it is playing
   *
   * @method playPause
   *
   */


  CanvasVideoPlayer.prototype.playPause = function () {
    if (this.playing) {
      this.pause();
    } else {
      this.play();
    }
  };
  /**
   * event loop to animate the next frame
   *
   * @method loop
   * @private
   *
   */


  CanvasVideoPlayer.prototype.loop = function (timestamp) {
    const self = this;
    const elapsed = (timestamp - this.lastTime) / 1000; // Render

    if (elapsed >= 1 / this.options.framesPerSecond) {
      this.video.currentTime = this.video.currentTime + elapsed;
      this.lastTime = timestamp; // Resync audio and video if they drift more than 300ms apart

      if (this.audio && Math.abs(this.audio.currentTime - this.video.currentTime) > 0.3) {
        this.video.currentTime = this.audio.currentTime;
      }
    } // Note: If we are at the end of the video stop


    if (this.video.currentTime >= this.video.duration || this.options.endTime && this.video.currentTime >= this.options.endTime) {
      if (this.options.resetOnLastFrame === true) {
        this.video.currentTime = this.startTime;
        this.playPause();
      }

      if (this.options.makeLoop === false) {
        this.playing = false;
      }
    }

    if (this.playing) {
      this.animationFrame = requestAnimationFrame(function (timestamp) {
        self.loop(timestamp || this.lastTime);
      });
    } else {
      cancelAnimationFrame(this.animationFrame);
    }
  };
  /**
   * draw a frame from video using canvas drawImage video
   *
   * @method loop
   * @private
   *
   */


  CanvasVideoPlayer.prototype.drawFrame = function () {
    if (this.video.readyState < 3) {
      return;
    }

    if (this.options.cropArray && this.options.cropArray.length) {
      const [x1, y1, x2, y2] = this.options.cropArray;
      const width = x2 - x1;
      const height = y2 - y1;
      this.ctx.drawImage(this.video, x1, y1, width, height, 0, 0, this.width, this.height);
    } else {
      this.ctx.drawImage(this.video, 0, 0, this.width, this.height);
    }
  };

  var _default = CanvasVideoPlayer;
  _exports.default = _default;
});